/* ---------------------------------------------------------------------------------------------------
   Workers' Compensation Registry
   Two jobs, both purely presentational:
     1. Highlight the most recently clicked letter in the A–Z nav.
     2. Optional in-browser search / filtering over the entries already rendered by Liquid.
   Nothing here needs touching when registry entries are added, edited or removed.
--------------------------------------------------------------------------------------------------- */

(function () {
  'use strict';

  function normalise(value) {
    return (value || '').trim().toLowerCase();
  }

  function initRegistry(root) {
    if (root.dataset.wcrReady === 'true') return;
    root.dataset.wcrReady = 'true';

    var nav = root.querySelector('[data-wcr-az]');
    var links = nav ? Array.prototype.slice.call(nav.querySelectorAll('[data-wcr-az-letter]')) : [];
    var groups = Array.prototype.slice.call(root.querySelectorAll('[data-wcr-group]'));
    var entries = Array.prototype.slice.call(root.querySelectorAll('[data-wcr-entry]'));
    var counts = Array.prototype.slice.call(root.querySelectorAll('[data-wcr-count]'));
    var noResults = root.querySelector('[data-wcr-no-results]');
    var filters = root.querySelector('[data-wcr-filters]');
    var padded = counts.length > 0 && /^0\d$/.test(counts[0].textContent.trim());

    /* --- 1. A–Z highlight ---------------------------------------------------------------- */

    links.forEach(function (link) {
      if (link.tagName !== 'A') return;
      link.addEventListener('click', function () {
        links.forEach(function (other) {
          other.classList.remove('is-active');
        });
        link.classList.add('is-active');
      });
    });

    if (!filters) return;

    /* --- 2. Filters ---------------------------------------------------------------------- */

    var searchInput = filters.querySelector('[data-wcr-search]');
    var coverageSelect = filters.querySelector('[data-wcr-filter="coverage"]');
    var incentiveSelect = filters.querySelector('[data-wcr-filter="incentive"]');
    var resets = Array.prototype.slice.call(root.querySelectorAll('[data-wcr-reset]'));

    function fillSelect(select, key) {
      if (!select) return;
      var seen = [];
      entries.forEach(function (entry) {
        var value = (entry.dataset[key] || '').trim();
        if (value && seen.indexOf(value) === -1) seen.push(value);
      });
      seen.sort(function (a, b) {
        return a.localeCompare(b);
      });
      seen.forEach(function (value) {
        var option = document.createElement('option');
        option.value = normalise(value);
        option.textContent = value;
        select.appendChild(option);
      });
      // A filter with a single option filters nothing — leave it out of the UI.
      if (seen.length < 2) select.hidden = true;
    }

    fillSelect(coverageSelect, 'coverage');
    fillSelect(incentiveSelect, 'incentive');

    function formatCount(value) {
      return padded && value < 10 ? '0' + value : String(value);
    }

    function apply() {
      var term = normalise(searchInput ? searchInput.value : '');
      var coverage = coverageSelect ? coverageSelect.value : '';
      var incentive = incentiveSelect ? incentiveSelect.value : '';
      var active = coverage !== '' || incentive !== '' || term !== '';
      var visible = 0;
      var lettersWithMatches = [];

      entries.forEach(function (entry) {
        var matches =
          (term === '' || (entry.dataset.search || '').indexOf(term) !== -1) &&
          (coverage === '' || normalise(entry.dataset.coverage) === coverage) &&
          (incentive === '' || normalise(entry.dataset.incentive) === incentive);

        entry.hidden = !matches;
        if (!matches) return;

        visible += 1;
        var group = entry.closest('[data-wcr-group]');
        var letter = group && group.dataset.wcrGroup;
        if (letter && lettersWithMatches.indexOf(letter) === -1) lettersWithMatches.push(letter);
      });

      groups.forEach(function (group) {
        group.hidden = lettersWithMatches.indexOf(group.dataset.wcrGroup) === -1;
      });

      links.forEach(function (link) {
        if (link.tagName !== 'A') return;
        var enabled = lettersWithMatches.indexOf(link.dataset.wcrAzLetter) !== -1;
        link.classList.toggle('wcr__az-inert', !enabled);
        link.setAttribute('aria-disabled', enabled ? 'false' : 'true');
        if (!enabled) link.classList.remove('is-active');
      });

      counts.forEach(function (node) {
        node.textContent = formatCount(visible);
      });

      if (noResults) noResults.hidden = visible !== 0;
      resets.forEach(function (button) {
        button.hidden = !active;
      });
    }

    function reset() {
      if (searchInput) searchInput.value = '';
      if (coverageSelect) coverageSelect.value = '';
      if (incentiveSelect) incentiveSelect.value = '';
      apply();
      if (searchInput) searchInput.focus();
    }

    if (searchInput) searchInput.addEventListener('input', apply);
    if (coverageSelect) coverageSelect.addEventListener('change', apply);
    if (incentiveSelect) incentiveSelect.addEventListener('change', apply);
    resets.forEach(function (button) {
      button.addEventListener('click', reset);
    });

    // Don't jump to a letter that has no visible entries.
    links.forEach(function (link) {
      if (link.tagName !== 'A') return;
      link.addEventListener('click', function (event) {
        if (link.getAttribute('aria-disabled') === 'true') event.preventDefault();
      });
    });

    filters.hidden = false;
    apply();
  }

  function copyLink(url) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(url);
    }
    // Fallback for older browsers.
    return new Promise(function (resolve) {
      var el = document.createElement('textarea');
      el.value = url;
      el.setAttribute('readonly', '');
      el.style.position = 'absolute';
      el.style.left = '-9999px';
      document.body.appendChild(el);
      el.select();
      try { document.execCommand('copy'); } catch (e) {}
      document.body.removeChild(el);
      resolve();
    });
  }

  function flashCopied(el) {
    el.classList.add('is-copied');
    window.setTimeout(function () {
      el.classList.remove('is-copied');
    }, 2200);
  }

  function initShare() {
    if (window.__wcrShareInit) return;
    window.__wcrShareInit = true;

    // Instagram / TikTok — copy the link instead of a dead share endpoint.
    document.querySelectorAll('[data-wcr-copy]').forEach(function (link) {
      link.addEventListener('click', function (event) {
        event.preventDefault();
        var container = link.closest('[data-wcr-share]');
        var url = (container && container.getAttribute('data-wcr-share-url')) || window.location.href;
        copyLink(url).then(function () {
          flashCopied(link);
        });
      });
    });

    // Bottom "Share This Registry" — native share sheet, or copy as a fallback.
    document.querySelectorAll('[data-wcr-share-native]').forEach(function (button) {
      button.addEventListener('click', function () {
        var url = button.getAttribute('data-wcr-share-url') || window.location.href;
        var data = {
          title: button.getAttribute('data-wcr-share-title') || document.title,
          text: button.getAttribute('data-wcr-share-text') || '',
          url: url
        };
        if (navigator.share) {
          navigator.share(data).catch(function () {});
        } else {
          var original = button.textContent;
          copyLink(url).then(function () {
            button.textContent = '\u2713 Link copied';
            window.setTimeout(function () {
              button.textContent = original;
            }, 2000);
          });
        }
      });
    });
  }

  function initZohoResize() {
    var iframes = document.querySelectorAll('.wcr-form-card__embed iframe');
    if (!iframes.length) return;
    var isZoho = false;

    iframes.forEach(function (iframe) {
      var src = iframe.getAttribute('src') || '';
      if (src.indexOf('formperma') === -1) return; // only touch Zoho form iframes
      isZoho = true;
      iframe.style.transition = 'height 0.35s ease';
      // Ask Zoho to broadcast its real height via postMessage.
      if (src.indexOf('zf_rszfm') === -1) {
        iframe.setAttribute('src', src + (src.indexOf('?') > -1 ? '&' : '?') + 'zf_rszfm=1');
      }
    });

    if (!isZoho || window.__wcrZohoInit) return;
    window.__wcrZohoInit = true;

    window.addEventListener('message', function (event) {
      var data = event.data;
      if (!data || typeof data !== 'string') return;
      var parts = data.split('|');
      if (parts.length < 2) return;
      var perma = parts[0];
      var height = (parseInt(parts[1], 10) + 15) + 'px';
      if (height === '15px' || height === 'NaNpx') return;
      document.querySelectorAll('.wcr-form-card__embed iframe').forEach(function (iframe) {
        var src = iframe.getAttribute('src') || '';
        if (src.indexOf('formperma') > -1 && src.indexOf(perma) > -1 && iframe.style.height !== height) {
          iframe.style.height = height;
        }
      });
    }, false);
  }

  function initAll() {
    document.querySelectorAll('[data-wcr-registry]').forEach(function (root) {
      initRegistry(root.closest('.wcr') || root);
    });
    initShare();
    initZohoResize();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-run when the section is re-rendered inside the theme editor.
  document.addEventListener('shopify:section:load', initAll);
})();
